import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageShell } from "@/components/marketing/PageShell";
import { api, formatApiError } from "@/lib/api";
import { PURPOSE_LABELS } from "@/lib/cases";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const F = ({ label, children }) => (
  <div className="flex flex-col gap-2"><Label className="font-mono-label text-[10px] font-semibold text-oath-secondary">{label}</Label>{children}</div>
);

const COPY = {
  business: { title: "Create a business account", intro: "Sign up your organization. Every business is reviewed by our team before it can order verifications.", path: "/signup/business" },
  personal: { title: "Create a free personal account", intro: "See verification requests about you, give or decline consent, upload proof of address and track results. Always free.", path: "/signup/personal" },
};

export default function Signup({ kind }) {
  const business = kind === "business";
  const [f, setF] = useState({ full_name: "", email: "", password: "", company: "", license_number: "", industry: "", dob: "", phone: "", requested_purposes: [], website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const togglePurpose = (p) => setF({ ...f, requested_purposes: f.requested_purposes.includes(p) ? f.requested_purposes.filter((x) => x !== p) : [...f.requested_purposes, p] });

  const submit = async (e) => {
    e.preventDefault();
    if (business && !f.requested_purposes.length) return toast.error("Choose at least one purpose.");
    setBusy(true);
    try {
      const keys = business ? ["full_name", "email", "password", "company", "license_number", "industry", "requested_purposes", "website"] : ["full_name", "email", "password", "dob", "phone", "website"];
      const { data } = await api.post(COPY[kind].path, Object.fromEntries(keys.map((k) => [k, f[k]])));
      setDone(data.message);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell eyebrow={business ? "— FOR BUSINESSES —" : "— FOR INDIVIDUALS —"} title={COPY[kind].title}>
      <div className="max-w-2xl rounded-3xl border border-gray-200 bg-white p-6 sm:p-8">
        {done ? (
          <div className="text-center" data-testid="signup-success">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
            <p className="mt-4 text-sm text-oath-secondary">{done}</p>
            {business && <p className="mt-2 text-sm text-oath-secondary">After you sign in you'll see your approval status. We'll email you when your business is approved.</p>}
            <Link to="/signin" className="mt-6 inline-block rounded-full bg-oath-ink px-6 py-3 font-mono-label text-[11px] font-semibold text-white" data-testid="signup-go-signin">SIGN IN</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="grid gap-5" data-testid={`signup-${kind}-form`}>
            <p className="text-sm text-oath-secondary">{COPY[kind].intro}</p>
            <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="absolute -left-[9999px] h-0 w-0 opacity-0" aria-hidden="true" />
            <div className="grid gap-4 sm:grid-cols-2">
              <F label="FULL NAME"><Input required value={f.full_name} onChange={set("full_name")} data-testid="signup-name" /></F>
              <F label={business ? "WORK EMAIL" : "EMAIL"}><Input required type="email" value={f.email} onChange={set("email")} data-testid="signup-email" /></F>
              {business ? (<>
                <F label="COMPANY / LEGAL NAME"><Input required value={f.company} onChange={set("company")} data-testid="signup-company" /></F>
                <F label="LICENSE / REGISTRATION #"><Input required value={f.license_number} onChange={set("license_number")} data-testid="signup-license" /></F>
                <F label="INDUSTRY"><Input required value={f.industry} onChange={set("industry")} placeholder="e.g. Property & casualty insurance" data-testid="signup-industry" /></F>
              </>) : (<>
                <F label="DATE OF BIRTH"><Input required type="date" value={f.dob} onChange={set("dob")} data-testid="signup-dob" /></F>
                <F label="MOBILE PHONE"><Input required value={f.phone} onChange={set("phone")} placeholder="(555) 123-4567" data-testid="signup-phone" /></F>
              </>)}
              <F label="PASSWORD (10+ CHARACTERS, LETTERS AND NUMBERS)"><Input required type="password" minLength={10} value={f.password} onChange={set("password")} data-testid="signup-password" /></F>
            </div>
            {business && (
              <div>
                <Label className="font-mono-label text-[10px] font-semibold text-oath-secondary">WHY YOU'LL VERIFY ADDRESSES</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(PURPOSE_LABELS).map(([k, l]) => (
                    <button type="button" key={k} onClick={() => togglePurpose(k)} data-testid={`signup-purpose-${k}`}
                      className={`rounded-full border px-4 py-2 text-xs ${f.requested_purposes.includes(k) ? "border-oath-ink bg-oath-ink text-white" : "border-gray-200 text-oath-ink"}`}>{l}</button>
                  ))}
                </div>
              </div>
            )}
            {!business && <p className="text-xs text-oath-muted">We use your date of birth and phone only to match verification requests that are about you.</p>}
            <button disabled={busy} className="w-fit rounded-full bg-oath-ink px-6 py-3.5 font-mono-label text-[11px] font-semibold text-white disabled:opacity-60" data-testid="signup-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "CREATE ACCOUNT"}
            </button>
            <p className="text-xs text-oath-muted">
              {business ? <>Signing up for yourself? <Link to="/signup/personal" className="text-oath-accent underline">Create a free personal account</Link>.</> : <>Signing up a company? <Link to="/signup" className="text-oath-accent underline">Create a business account</Link>.</>}
              {" "}By continuing you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.
            </p>
          </form>
        )}
      </div>
    </PageShell>
  );
}
